import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, Loader2, Save, Wand2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SELECT = "bg-black/40 border border-white/15 rounded-md px-2.5 py-1.5 text-[12px] text-white/90 focus:outline-none focus:border-violet-300/60 transition-colors min-w-0 w-full sm:w-64";

const modelLabel = (models, id) => (models.find((m) => m.id === id) || { label: id }).label;

/** Settings → LLM Keys → AI models: one Claude model per AI feature + a default for all. */
export function ModelPickerCard() {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data: d } = await api.get("/admin/llm-keys/models");
        setData(d);
        setDraft({ default_model: d.default_model, ...Object.fromEntries(d.features.map((f) => [f.key, f.model])) });
    };
    useEffect(() => { load().catch(() => toast.error("Failed to load AI models")); }, []);

    const put = async (body, msg) => {
        setBusy(true);
        try {
            await api.put("/admin/llm-keys/models", body);
            toast.success(msg);
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <div className="mt-8 text-[11px] text-white/45" data-testid="llm-models-loading">Loading AI models…</div>;
    const changed = data.features.filter((f) => (draft[f.key] || null) !== (f.model || null));
    const defaultChanged = draft.default_model !== data.default_model;
    const save = () => put({ default_model: draft.default_model, features: Object.fromEntries(changed.map((f) => [f.key, draft[f.key] || null])) }, "AI models saved");
    const applyAll = () => put({ default_model: draft.default_model, reset_all: true }, `Every AI feature now uses ${modelLabel(data.models, draft.default_model)}`);

    return (
        <section className="mt-8 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6" data-testid="llm-models-card">
            <header className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                    <h2 className="text-sm font-semibold text-white inline-flex items-center gap-2"><Cpu className="w-4 h-4 text-violet-300" /> AI models</h2>
                    <p className="text-[12px] text-white/50 mt-1 max-w-xl">The Claude model each AI feature uses. Agents keep their own model on the Agents page.</p>
                </div>
                <button onClick={save} disabled={busy || (!changed.length && !defaultChanged)} data-testid="llm-models-save-btn"
                        className="px-3 py-1.5 bg-violet-500/90 hover:bg-violet-500 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
                </button>
            </header>
            <DefaultRow data={data} value={draft.default_model} onChange={(v) => setDraft((d) => ({ ...d, default_model: v }))} onApplyAll={applyAll} busy={busy} />
            <ul className="mt-3 divide-y divide-white/[0.05]" data-testid="llm-models-features">
                {data.features.map((f) => (
                    <FeatureRow key={f.key} feature={f} models={data.models} defaultModel={draft.default_model}
                                value={draft[f.key] || ""} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v || null }))} />
                ))}
            </ul>
            {data.updated_at && <p className="mt-3 text-[10px] text-white/35" data-testid="llm-models-updated">Last changed {new Date(data.updated_at).toLocaleString()} by {data.updated_by}</p>}
        </section>
    );
}

function DefaultRow({ data, value, onChange, onApplyAll, busy }) {
    return (
        <div className="flex items-center gap-3 flex-wrap bg-violet-400/[0.05] border border-violet-300/20 rounded-lg px-3 py-2.5">
            <div className="flex-1 min-w-[180px]">
                <div className="text-[12px] text-white/90 font-medium">Default for all features</div>
                <div className="text-[11px] text-white/45">Used by every feature set to "Use default".</div>
            </div>
            <ModelSelect models={data.models} value={value} onChange={onChange} testId="llm-models-default-select" />
            <button onClick={onApplyAll} disabled={busy} data-testid="llm-models-apply-all-btn"
                    className="px-2.5 py-1.5 border border-white/15 rounded-md text-[11px] hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
                <Wand2 className="w-3 h-3" /> Set all features to this
            </button>
        </div>
    );
}

function FeatureRow({ feature, models, defaultModel, value, onChange }) {
    const chosen = models.find((m) => m.id === (value || defaultModel));
    return (
        <li className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`llm-models-row-${feature.key}`}>
            <div className="flex-1 min-w-[180px]">
                <div className="text-[12px] text-white/85">{feature.label}</div>
                <div className="text-[11px] text-white/40">{feature.description}</div>
            </div>
            <div className="w-full sm:w-64">
                <ModelSelect models={models} value={value} onChange={onChange} testId={`llm-models-select-${feature.key}`}
                             defaultOption={`Use default (${modelLabel(models, defaultModel)})`} />
                <ModelNote model={chosen} testId={`llm-models-note-${feature.key}`} />
            </div>
        </li>
    );
}

function ModelSelect({ models, value, onChange, testId, defaultOption }) {
    return (
        <select className={SELECT} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
            {defaultOption && <option value="">{defaultOption}</option>}
            {models.map((m) => (
                <option key={m.id} value={m.id}>{m.label}{m.recommended ? " (recommended)" : ""}{m.retires ? ` — retires ${m.retires}` : ""}</option>
            ))}
        </select>
    );
}

function ModelNote({ model, testId }) {
    if (!model) return null;
    const notes = [model.retires && `Retires ${model.retires}`, model.fixed_sampling && "Runs at its default temperature",
                   model.min_output_tokens && `Reasons first — answers get ≥ ${model.min_output_tokens} tokens`].filter(Boolean);
    if (!notes.length) return null;
    return <div className={`mt-1 text-[10px] ${model.retires ? "text-amber-200/80" : "text-white/35"}`} data-testid={testId}>{notes.join(" · ")}</div>;
}
